import { PendingImage } from "@components/camera-roll-image";
import { ChipMarkers } from "@components/frame-editor/chip-markers";
import { FaceHighlighter } from "@components/frame-editor/face-highlight";
import { Frame } from "@components/frame-editor/frame";
import {
  PanSizeComponent,
  TransformType,
} from "@components/frame-editor/pan-size-component";
import { ImageRelationshipNavigator } from "@components/image-relationship-navigator";
import { ImagesType } from "@features/images/type";
import { useImage } from "@features/images/use-images";
import { useImageActions } from "@features/images/use-images-actions";
import { getImageSizeFromSteps } from "@features/images/utils";
import { useChipHandlers } from "@features/studio/use-chip-handlers";
import { renderImage } from "@features/studio/use-render-image";
import {
  emptyStudioState,
  StudioContext,
  StudioRedoableStateType,
  StudioStateType,
  useStudio,
} from "@features/studio/use-studio";
import { useEffectPlease, useNavigateAlt } from "@features/utils";
import { useIsMobile } from "@features/utils/use-mobile";
import { useSavedState } from "@features/utils/use-saved-state";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/16/solid";
import { Spinner } from "@radix-ui/themes";
import { Page, usePageTitle } from "@views/layout/layout";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { StudioSidebar } from "./components";
import { StudioMobileActions } from "./components/studio-mobile-actions";
import { Preview } from "./preview";
import { StudioMobilePreview } from "./preview-mobile";

let saveDebounce: any;

export const Studio = (props: { id?: string }) => {
  usePageTitle("Photographe.ai - Studio");

  const paramId = useParams<{ id: string }>().id;
  const id = props.id || paramId;
  const { image } = useImage(id!);

  if (!image) {
    return (
      <Page verticalCenter horizontalCenter>
        <div className="w-full h-40 flex items-center justify-center">
          <Spinner />
        </div>
      </Page>
    );
  }

  return <StudioContent image={image} id={id!} key={id!} />;
};

export const StudioContent = ({
  id,
  image,
}: {
  id: string;
  image: ImagesType;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigateAlt();
  const queryClient = useQueryClient();
  const currentIdRef = useRef<string | undefined>(id);
  const isMobile = useIsMobile();

  const { site } = useParams();

  // Update component when ID prop changes
  useEffect(() => {
    if (id !== currentIdRef.current) {
      currentIdRef.current = id;
    }
  }, [id]);

  const [studio, setStudio] = useSavedState<StudioStateType>(`studio-${id}`, {
    ...emptyStudioState,
    frame: { width: image.size.width, height: image.size.height },
    ..._.pick(
      image.steps?.find((step) => step.mode === "edits") || {},
      "transform",
      "effects",
      "frame",
      "drawing"
    ),
  });
  const [undos, setUndos] = useSavedState<{
    redos: StudioRedoableStateType[];
    undos: StudioRedoableStateType[];
  }>(`studio-undos-${id}`, { undos: [], redos: [] });

  useEffectPlease(() => {
    console.log("Initializing studio state for image ID:", id);
    setStudio((studio) => ({
      ...studio,
      ..._.pick(
        image.steps?.find((step) => step.mode === "edits") || {},
        "transform",
        "effects",
        "frame",
        "drawing"
      ),
    }));
  }, []);

  const [isHovering, setIsHovering] = useState(false);

  // Find adjacent images in the camera roll
  const [adjacentImages, setAdjacentImages] = useState<{
    prev: string | null;
    next: string | null;
  }>({ prev: null, next: null });

  const findAdjacentImages = useCallback(() => {
    if (!id) return { prev: null, next: null };

    const allImagesQueryKeys = queryClient
      .getQueryCache()
      .getAll()
      .filter(
        (query) =>
          query.queryKey[0] === "images" && query.queryKey[1] === "list"
      )
      .map((query) => query.queryKey);

    for (const queryKey of allImagesQueryKeys) {
      const images = (
        queryClient.getQueryData<{ pages: { data: ImagesType[] }[] }>(queryKey)
          ?.pages || []
      )
        .flatMap((page) => page.data)
        .filter((a) => a.status !== "failed");

      if (images && Array.isArray(images)) {
        const currentIndex = images.findIndex((img) => img.id === id);

        if (currentIndex !== -1) {
          const prev = currentIndex > 0 ? images[currentIndex - 1].id : null;
          const next =
            currentIndex < images.length - 1
              ? images[currentIndex + 1].id
              : null;

          return { prev, next };
        }
      }
    }

    return { prev: null, next: null };
  }, [id, queryClient]);

  useEffect(() => {
    setAdjacentImages(findAdjacentImages());
  }, [findAdjacentImages]);

  const navigateToPrev = useCallback(() => {
    if (studio?.is_editing) return;
    if (adjacentImages.prev) {
      navigate("/" + site + "/studio/" + adjacentImages.prev);
    }
  }, [adjacentImages.prev, navigate, site, studio?.is_editing]);

  const navigateToNext = useCallback(() => {
    if (studio?.is_editing) return;
    if (adjacentImages.next) {
      navigate("/" + site + "/studio/" + adjacentImages.next);
    }
  }, [adjacentImages.next, navigate, site, studio?.is_editing]);

  useEffectPlease(() => {
    // If we're editing, don't navigate
    if (studio?.is_editing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (e.key === "ArrowLeft") {
        navigateToPrev();
      } else if (e.key === "ArrowRight") {
        navigateToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigateToPrev, navigateToNext]);

  const contextSetStudio = useCallback(
    (newState: Partial<StudioStateType>) => {
      setStudio((studio) => ({
        ...studio,
        ..._.cloneDeep(newState),
      }));
    },
    [setStudio]
  );

  // Memoize the entire context value
  const contextValue = useMemo(
    () => ({
      setUndos: setUndos as any,
      undos,
      studio,
      setStudio: contextSetStudio,
    }),
    [setUndos, undos, studio, contextSetStudio]
  );

  return (
    <StudioContext.Provider value={contextValue}>
      <Page verticalCenter={false} className="fixed md:relative">
        <div className="w-full flex flex-col-reverse md:space-y-0 md:space-x-4 md:flex-row md:pr-4">
          <div className="pt-4 md:pt-0 hidden md:block">
            <StudioSidebar
              image={image}
              changeTool={() => {}}
              closestImageId={
                adjacentImages.next || adjacentImages.prev || undefined
              }
            />
          </div>
          <div className="md:max-h-screen md:grow space-y-8 overflow-visible md:overflow-hidden relative sticky md:top-0 md:relative z-50 top-0 pt-0 pb-1 md:pb-0 backdrop-blur-lg rounded-lg rounded-bl-sm">
            <div
              className="relative w-full px-0 h-available-screen-height relative flex justify-center items-start md:items-center overflow-visible md:overflow-hidden user-select-none"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
              }}
            >
              {/* Previous button - Desktop only */}
              {!isMobile && !studio?.is_editing && (
                <button
                  className={twMerge(
                    "absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-2 transition-opacity z-10",
                    isHovering && adjacentImages.prev
                      ? "opacity-70 hover:opacity-100"
                      : "opacity-0"
                  )}
                  onClick={navigateToPrev}
                  disabled={!adjacentImages.prev}
                  aria-label={t("general.previous")}
                >
                  <ChevronLeftIcon className="h-8 w-8 text-white" />
                </button>
              )}

              {/* Next button - Desktop only */}
              {!isMobile && !studio?.is_editing && (
                <button
                  className={twMerge(
                    "absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-2 transition-opacity z-10",
                    isHovering && adjacentImages.next
                      ? "opacity-70 hover:opacity-100"
                      : "opacity-0"
                  )}
                  onClick={navigateToNext}
                  disabled={!adjacentImages.next}
                  aria-label={t("general.next")}
                >
                  <ChevronRightIcon className="h-8 w-8 text-white" />
                </button>
              )}

              {/* Mobile floating actions */}
              {isMobile &&
                !studio?.is_editing &&
                image.status === "completed" && (
                  <StudioMobileActions image={image} lockedActions={[]} />
                )}

              {/* Mobile preview - touch-friendly with swipe navigation */}
              {isMobile && image.status === "completed" && (
                <StudioMobilePreview
                  image={image}
                  onNavigatePrev={navigateToPrev}
                  onNavigateNext={navigateToNext}
                  hasPrev={!!adjacentImages.prev}
                  hasNext={!!adjacentImages.next}
                />
              )}

              {/* Desktop preview with Frame and Editor */}
              {!isMobile &&
                (!studio?.is_editing || image.status !== "completed") && (
                  <>
                    {image.status === "completed" && (
                      <StudioMobilePreview image={image} />
                    )}

                    {image.status === "pending" && (
                      <Frame
                        readonly
                        width={image.size.width}
                        height={image.size.height}
                      >
                        <PanSizeComponent
                          width={image.size.width}
                          height={image.size.height}
                        >
                          <PendingImage image={image} />
                        </PanSizeComponent>
                      </Frame>
                    )}

                    {image.status === "failed" && (
                      <div className="flex items-center justify-center h-full">
                        Image creation failed.
                      </div>
                    )}
                  </>
                )}

              {!isMobile &&
                !!studio?.is_editing &&
                image.status === "completed" && (
                  <Editor image={image} key={image.id} />
                )}
            </div>

            <div className="z-50 absolute -bottom-4 hidden md:block md:bottom-0 md:left-0">
              <ImageRelationshipNavigator currentImageId={id} />
            </div>
          </div>
        </div>
      </Page>
    </StudioContext.Provider>
  );
};

export const Editor = ({ image }: { image: ImagesType }) => {
  const { edit } = useImageActions(image.id);
  const { studio, setStudio, undo, redo, undos } = useStudio();
  const studioTool = useRef(studio?.tool);
  studioTool.current = studio?.tool;

  // Chip handlers for point-and-click edits
  const {
    chips,
    selectedChipId,
    handleAddChip,
    handleMoveChip,
    handleSelectChip,
  } = useChipHandlers();

  // Handle chip mode toggle from Frame toolbar
  const handleChipModeChange = useCallback(
    (active: boolean) => {
      setStudio({ edit_tool: active ? "chips" : "edit" });
    },
    [setStudio]
  );

  const onTransformChangeCallback = useCallback(
    (transform: TransformType) => {
      if (
        studioTool.current === "crop-extend" ||
        studioTool.current === "photo-effects"
      ) {
        setStudio({
          transform,
        });
      }
    },
    [setStudio, studioTool]
  );

  const savedEdits = image.steps?.find((step) => step.mode === "edits");
  const hasChanges = !_.isEqual(
    _.pick(studio, ["transform", "effects", "frame", "drawing"]),
    _.pick(savedEdits, ["transform", "effects", "frame", "drawing"])
  );

  const saveEdits = useCallback(async () => {
    if (!hasChanges) return;
    await edit(
      image,
      _.pick(studio, ["transform", "effects", "frame", "drawing"]),
      undos,
      await renderImage(image, studio, {
        format: "jpg",
        frame: true,
        effects: true,
        transform: true,
        ignore_edited_image: true,
      })
    );
  }, [edit, image, studio, undos, hasChanges]);

  useEffectPlease(() => {
    if (!hasChanges) return;
    if (!(studio.tool === "crop-extend" || studio.tool === "photo-effects"))
      return;
    clearTimeout(saveDebounce);
    saveDebounce = setTimeout(async () => {}, 2000);

    return () => saveEdits();
  }, [hasChanges]);

  useEffectPlease(() => {
    saveEdits();
  }, [studio.tool]);

  if (!studio) return null;

  const noEditSize = getImageSizeFromSteps(
    image?.steps.filter((a) => a.mode !== "edits")
  );

  // Determine drawing mode based on studio tool
  const getDrawingMode = (): "edit" | "erase" | undefined => {
    if (studio.tool !== "edit-objects") return undefined;
    switch (studio.edit_tool) {
      case "edit":
      case "chips": // Chips mode uses edit drawing mode for toolbar visibility
        return "edit";
      case "remove":
        return "erase";
      default:
        return undefined;
    }
  };

  return (
    <Frame
      width={
        studio.tool !== "crop-extend" && studio.tool !== "photo-effects"
          ? image.size.width
          : studio.frame.width
      }
      height={
        studio.tool !== "crop-extend" && studio.tool !== "photo-effects"
          ? image.size.height
          : studio.frame.height
      }
      refDrawingSVG={(drawing_svg_ref: React.RefObject<SVGSVGElement>) =>
        setStudio({ drawing_svg_ref })
      }
      onCommitDrawing={(drawing) => {
        setStudio({ drawing });
      }}
      drawingMode={getDrawingMode()}
      drawing={studio.drawing}
      onUndo={undos.undos?.length ? undo : undefined}
      onRedo={undos.redos?.length ? redo : undefined}
      // Chip mode props
      chipMode={studio.tool === "edit-objects" && studio.edit_tool === "chips"}
      chips={chips}
      selectedChipId={selectedChipId}
      onChipModeChange={handleChipModeChange}
      onSelectChip={handleSelectChip}
    >
      <PanSizeComponent
        readonly={
          studio.tool !== "crop-extend" && studio.tool !== "photo-effects"
        }
        transform={
          studio.tool !== "crop-extend" && studio.tool !== "photo-effects"
            ? { x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0, changed: false }
            : studio.transform
        }
        onTransformChange={onTransformChangeCallback}
        width={noEditSize.width}
        height={noEditSize.height}
        className={"group/frame"}
      >
        {false && (
          <FaceHighlighter
            image={image}
            active={studio.tool === "edit-objects"}
            width={noEditSize.width}
            height={noEditSize.height}
            className="opacity-0 group-hover/frame:opacity-100"
          />
        )}

        {/* Chip Markers */}
        {studio.edit_tool === "chips" && studio.tool === "edit-objects" && (
          <ChipMarkers
            width={noEditSize.width}
            height={noEditSize.height}
            chips={chips || []}
            active={true}
            onAddChip={handleAddChip}
            onMoveChip={handleMoveChip}
          />
        )}
        <Preview
          image={image}
          filter={studio.effects.filter}
          cursorSettings={studio.effects.adjustements}
        />
      </PanSizeComponent>
    </Frame>
  );
};
